"use client";

import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type AiChatContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

const AiChatContext = createContext<AiChatContextValue | null>(null);

/**
 * 診察セクション限定の AI チャットパネル開閉状態。
 * 開閉トリガ(診察ページ内の「AIに相談」ボタン)とパネル本体(レイアウト層の伸縮パネル)は
 * React ツリー上で親子関係にないため、consults レイアウトに Context を置いて橋渡しする。
 */
export const AiChatProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);
  return (
    <AiChatContext.Provider value={value}>{children}</AiChatContext.Provider>
  );
};

export const useAiChat = (): AiChatContextValue => {
  const ctx = useContext(AiChatContext);
  if (!ctx) {
    throw new Error("useAiChat must be used within AiChatProvider");
  }
  return ctx;
};
